import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, getDocs, addDoc, Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { UserProfile, Relationship } from '../types';
import StudentCard from '../components/StudentCard';
import { UserPlus, Search, X, Loader2, Mail, CheckCircle2, AlertCircle as AlertCircleIcon, Archive, Users, History, LayoutGrid, List, Filter, SortAsc, ChevronDown, Copy, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../lib/translations';
import Tooltip from '../components/Tooltip';

interface StudentsProps {
  profile: UserProfile | null;
}

const Students: React.FC<StudentsProps> = ({ profile }) => {
  const [students, setStudents] = useState<Relationship[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<string>('name-az');
  const [filterBy, setFilterBy] = useState<string>('all');
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [copied, setCopied] = useState(false);

  // Persistence of view mode
  useEffect(() => {
    const saved = localStorage.getItem('students_view_mode') as 'grid' | 'list';
    if (saved) setViewMode(saved);
  }, []);

  const handleViewModeChange = (mode: 'grid' | 'list') => {
    setViewMode(mode);
    localStorage.setItem('students_view_mode', mode);
  };

  const fetchStudents = async () => {
    if (!profile) return;
    setLoading(true);
    try {
      const statusToFetch = showArchived ? 'dropped' : 'active';
      const q = query(collection(db, 'relationships'), where('teacherId', '==', profile.uid), where('status', '==', statusToFetch));
      const snap = await getDocs(q);
      const enriched = await Promise.all(snap.docs.map(async d => {
        const data = d.data() as Relationship;
        const isPending = data.studentId.startsWith('pending:');
        
        let userData = null;
        if (!isPending) {
          const userDoc = await getDoc(doc(db, 'users', data.studentId));
          userData = userDoc?.data();
        }

        const email = isPending ? data.studentId.split(':')[1] : (userData?.email || data.studentId);
        const name = userData?.displayName || email.split('@')[0];

        return { 
          ...data, 
          id: d.id, 
          studentFirstName: userData?.firstName || name.split(' ')[0],
          studentLastName: userData?.lastName || '',
          studentName: isPending ? `${name} (${t.common.pending})` : name, 
          studentEmail: email, 
          studentPhoto: userData?.photoURL || '' 
        };
      }));
      setStudents(enriched);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchStudents(); }, [profile, showArchived]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;

    setInviteLoading(true); 
    setInviteError('');
    try {
      const userQ = query(collection(db, 'users'), where('email', '==', email));
      const userSnap = await getDocs(userQ);
      
      let studentIdToUse = '';
      if (!userSnap.empty) {
        studentIdToUse = userSnap.docs[0].id;
      } else {
        studentIdToUse = 'pending:' + email;
      }

      const relQ = query(collection(db, 'relationships'), 
        where('teacherId', '==', profile.uid), 
        where('studentId', '==', studentIdToUse)
      );
      const relSnap = await getDocs(relQ);
      
      if (!relSnap.empty) { 
        setInviteError(t.students.inviteModal.duplicateError); 
        setInviteLoading(false); 
        return; 
      }

      await addDoc(collection(db, 'relationships'), { 
        teacherId: profile.uid, 
        studentId: studentIdToUse, 
        lessonsPaid: 0, 
        lessonsCompleted: 0, 
        startDate: Timestamp.now(), 
        status: 'active' 
      });

      setShowSuccess(true);
      fetchStudents();
    } catch (e) { 
      setInviteError(t.students.inviteFailed); 
    }
    setInviteLoading(false);
  };

  const magicLink = `${window.location.origin}/#/login`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(magicLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setShowSuccess(false);
    setInviteEmail('');
    setInviteError('');
  };

  const processedStudents = useMemo(() => {
    let result = students.filter(s => 
      s.studentName?.toLowerCase().includes(search.toLowerCase()) || 
      s.studentEmail?.toLowerCase().includes(search.toLowerCase())
    );

    // Filtering
    if (filterBy === 'out-of-credits') {
      result = result.filter(s => (s.lessonsPaid - s.lessonsCompleted) <= 0);
    } else if (filterBy === 'low-credits') {
      result = result.filter(s => {
        const balance = s.lessonsPaid - s.lessonsCompleted;
        return balance > 0 && balance <= 2;
      });
    } else if (filterBy === 'healthy') {
      result = result.filter(s => (s.lessonsPaid - s.lessonsCompleted) >= 3);
    }

    // Sorting
    result.sort((a, b) => {
      const creditsA = a.lessonsPaid - a.lessonsCompleted;
      const creditsB = b.lessonsPaid - b.lessonsCompleted;
      
      switch (sortBy) {
        case 'name-az':
          return (a.studentName || '').localeCompare(b.studentName || '');
        case 'name-za':
          return (b.studentName || '').localeCompare(a.studentName || '');
        case 'credits-low':
          return creditsA - creditsB;
        case 'credits-high':
          return creditsB - creditsA;
        case 'date-newest':
          return b.startDate.toMillis() - a.startDate.toMillis();
        case 'date-oldest':
          return a.startDate.toMillis() - b.startDate.toMillis();
        default:
          return 0;
      }
    });

    return result;
  }, [students, search, sortBy, filterBy]);

  return (
    <div className="space-y-10 max-w-6xl mx-auto pb-20">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
             <button 
               onClick={() => setShowArchived(false)}
               className={`text-sm font-black uppercase tracking-widest transition-colors ${!showArchived ? 'text-purple-600' : 'text-slate-400 hover:text-slate-600'}`}
             >
               {t.students.directory}
             </button>
             <span className="text-slate-300">/</span>
             <button 
               onClick={() => setShowArchived(true)}
               className={`flex items-center gap-2 text-sm font-black uppercase tracking-widest transition-colors ${showArchived ? 'text-purple-600' : 'text-slate-400 hover:text-slate-600'}`}
             >
               <History size={14} /> {t.common.archived}
             </button>
          </div>
          <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
            {showArchived ? `${t.common.archived} ${t.nav.students}` : t.students.directory}
          </h1>
          <p className="text-slate-500 font-medium">{showArchived ? "View students that have moved on from your studio." : t.students.manageRoster}</p>
        </div>
        
        <div className="flex items-center gap-3 self-end md:self-center">
          <div className="flex items-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-xl p-1 shadow-sm">
            <Tooltip content={t.students.cardView}>
              <button 
                onClick={() => handleViewModeChange('grid')}
                className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-purple-50 dark:bg-purple-500/10 text-purple-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
              >
                <LayoutGrid size={18} />
              </button>
            </Tooltip>
            <Tooltip content={t.students.listView}>
              <button 
                onClick={() => handleViewModeChange('list')}
                className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-purple-50 dark:bg-purple-500/10 text-purple-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
              >
                <List size={18} />
              </button>
            </Tooltip>
          </div>

          <button onClick={() => setIsModalOpen(true)} className="flex items-center justify-center gap-2 bg-purple-600 text-white px-6 py-2.5 rounded-xl font-bold transition-all shadow-xl shadow-purple-500/20 hover:bg-purple-700 hover:scale-105 active:scale-95">
            <UserPlus size={20} /> {t.students.inviteStudent}
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative group flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-purple-600 transition-colors" size={20} />
          <input 
            type="text" 
            placeholder={t.students.searchPlaceholder} 
            value={search} 
            onChange={(e) => setSearch(e.target.value)} 
            className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-lg py-3.5 px-12 text-sm font-medium focus:ring-4 focus:ring-purple-500/10 focus:border-purple-500 outline-none shadow-sm transition-all" 
          />
        </div>

        <div className="flex gap-3 shrink-0">
          <div className="relative flex items-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-lg px-3 shadow-sm hover:border-purple-500/30 transition-all">
            <Filter size={16} className="text-slate-400 mr-2" />
            <select 
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value)}
              className="bg-transparent text-sm font-bold text-slate-600 dark:text-slate-300 outline-none cursor-pointer py-3 appearance-none pr-6"
            >
              <option value="all">{t.students.filtering.all}</option>
              <option value="out-of-credits">{t.students.filtering.outOfCredits}</option>
              <option value="low-credits">{t.students.filtering.lowCredits}</option>
              <option value="healthy">{t.students.filtering.healthyBalance}</option>
            </select>
            <ChevronDown size={14} className="absolute right-3 pointer-events-none text-slate-400" />
          </div>

          <div className="relative flex items-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-lg px-3 shadow-sm hover:border-purple-500/30 transition-all">
            <SortAsc size={16} className="text-slate-400 mr-2" />
            <select 
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-transparent text-sm font-bold text-slate-600 dark:text-slate-300 outline-none cursor-pointer py-3 appearance-none pr-6"
            >
              <option value="name-az">{t.students.sorting.nameAZ}</option>
              <option value="name-za">{t.students.sorting.nameZA}</option>
              <option value="credits-low">{t.students.sorting.creditsLow}</option>
              <option value="credits-high">{t.students.sorting.creditsHigh}</option>
              <option value="date-newest">{t.students.sorting.dateNewest}</option>
              <option value="date-oldest">{t.students.sorting.dateOldest}</option>
            </select>
            <ChevronDown size={14} className="absolute right-3 pointer-events-none text-slate-400" />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-32 gap-4">
          <Loader2 className="animate-spin text-purple-600" size={40} />
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{t.students.updatingRoster}</p>
        </div>
      ) : (
        <div className={viewMode === 'grid' ? "grid sm:grid-cols-2 lg:grid-cols-3 gap-6" : "flex flex-col gap-3"}>
          {processedStudents.length === 0 ? (
            <div className="sm:col-span-2 lg:col-span-3 py-20 text-center border-2 border-dashed border-slate-200 dark:border-white/5 rounded-xl">
              {showArchived ? <Archive size={48} className="text-slate-200 dark:text-slate-800 mx-auto mb-4" /> : <Users size={48} className="text-slate-200 dark:text-slate-800 mx-auto mb-4" />}
              <p className="text-slate-400 font-bold italic">{t.students.noStudents}</p>
            </div>
          ) : (
            processedStudents.map(student => (
              <StudentCard 
                key={student.id} 
                relationship={student} 
                variant={viewMode}
                onClick={() => navigate(`/student/${student.studentId}`)} 
              />
            ))
          )}
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-300 mt-0 !mt-0">
          <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-xl border border-slate-200 dark:border-white/5 p-10 shadow-2xl relative overflow-hidden">
            <button 
              onClick={handleCloseModal} 
              className="absolute top-8 right-8 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-all active:scale-90"
            >
              <X size={24} />
            </button>

            {showSuccess ? (
              <div className="py-8 flex flex-col items-center justify-center text-center space-y-4 animate-in zoom-in duration-300">
                <div className="w-16 h-16 bg-teal-500/10 text-teal-600 rounded-lg flex items-center justify-center mb-2">
                  <CheckCircle2 size={40} strokeWidth={2.5} />
                </div>
                <h3 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white">{t.students.inviteModal.successTitle}</h3>
                <p className="text-slate-500 text-sm font-medium">{t.students.inviteModal.successDesc}</p>
                
                <div className="w-full pt-8 space-y-4">
                  <div className="text-left space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">{t.students.inviteModal.magicLinkTitle}</label>
                    <div className="flex gap-2">
                      <div className="flex-1 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg py-2.5 px-4 text-xs font-bold text-slate-500 truncate">
                        {magicLink}
                      </div>
                      <button 
                        onClick={handleCopyLink}
                        className={`flex items-center justify-center gap-2 px-4 rounded-lg font-black text-[10px] uppercase tracking-widest transition-all ${copied ? 'bg-teal-600 text-white' : 'bg-purple-600 text-white hover:bg-purple-700 shadow-lg shadow-purple-500/10'}`}
                      >
                        {copied ? <Check size={14} /> : <Copy size={14} />}
                        {copied ? t.common.copied : t.common.copy}
                      </button>
                    </div>
                    <p className="text-[9px] text-slate-400 font-medium italic mt-1 leading-relaxed">{t.students.inviteModal.magicLinkDesc}</p>
                  </div>
                  
                  <button 
                    onClick={handleCloseModal}
                    className="w-full py-4 bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white rounded-lg font-black text-[10px] uppercase tracking-[0.2em] transition-all hover:bg-slate-200 active:scale-95"
                  >
                    {t.common.done}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-8">
                  <div className="w-14 h-14 bg-purple-50 dark:bg-purple-900/30 rounded-lg flex items-center justify-center mb-6">
                    <UserPlus size={28} className="text-purple-600" />
                  </div>
                  <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tighter mb-2">{t.students.inviteModal.title}</h2>
                  <p className="text-slate-500 text-sm font-medium leading-relaxed">{t.students.inviteModal.desc}</p>
                </div>

                <form onSubmit={handleInvite} className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">{t.students.inviteModal.emailLabel}</label>
                    <div className="relative group">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-purple-600 transition-colors" size={18} />
                      <input 
                        type="email" 
                        required 
                        value={inviteEmail} 
                        onChange={(e) => setInviteEmail(e.target.value)} 
                        placeholder={t.students.inviteModal.emailPlaceholder} 
                        className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg py-3 pl-12 pr-4 text-sm font-black focus:ring-4 focus:ring-purple-500/10 focus:border-purple-500 outline-none transition-all shadow-sm" 
                      />
                    </div>
                  </div>

                  {inviteError && (
                    <div className="p-4 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-500/20 rounded-lg text-red-600 text-[11px] font-black uppercase tracking-widest flex items-center gap-3">
                      <AlertCircleIcon size={16} /> {inviteError}
                    </div>
                  )}

                  <button 
                    type="submit" 
                    disabled={inviteLoading} 
                    className="w-full py-4 bg-purple-600 text-white rounded-lg font-black text-[11px] uppercase tracking-[0.2em] transition-all shadow-xl shadow-purple-500/20 hover:bg-purple-700 active:scale-95 disabled:opacity-50 flex items-center justify-center gap-3"
                  >
                    {inviteLoading ? <Loader2 className="animate-spin" size={20} /> : t.students.inviteModal.submit}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Students;